import { Edit } from '@refinedev/mui'
import { Alert, Box, TextField, Autocomplete } from '@mui/material'
import { useForm } from '@refinedev/react-hook-form'
import { Controller } from 'react-hook-form'
import { useEffect } from 'react'
import { NZ_REGIONS, getCouncilsForRegion } from '@/constants/nzRegionsCouncils'
import {
  adminFacingMessage,
  locationDeleteErrorNotification,
} from '@/utils/adminFacingError'

export const RegionEdit = () => {
  const {
    saveButtonProps,
    refineCore: { formLoading, query },
    register,
    control,
    getValues,
    setValue,
    watch,
    formState: { errors },
  } = useForm({
    refineCoreProps: {
      action: 'edit',
      resource: 'regions',
      errorNotification: (error) => ({
        message: 'Could not save location',
        description:
          adminFacingMessage(error) ??
          'Something went wrong when editing the location. Please try again.',
        type: 'error',
      }),
    },
  })

  useEffect(() => {
    const additionalNames = getValues('additional_names')
    if (Array.isArray(additionalNames)) {
      setValue('additional_names', additionalNames.join(', '))
    }
  }, [formLoading, getValues, setValue])

  const isEmpty = (value: any) =>
    value === null || value === undefined || value === ''

  // Public base regions drive the public pages: only additional names stay editable.
  const isBaseRegion = !!query?.data?.data?.is_public_base

  const country = watch('country')
  const region = watch('region')
  const isNewZealand = country === 'New Zealand'

  // Get councils filtered by selected region
  const councilOptions = region ? getCouncilsForRegion(region) : []

  return (
    <Edit
      isLoading={formLoading}
      saveButtonProps={saveButtonProps}
      canDelete={!isBaseRegion}
      deleteButtonProps={{ errorNotification: locationDeleteErrorNotification }}
    >
      <Box
        component="form"
        sx={{ display: 'flex', flexDirection: 'column' }}
        autoComplete="off"
      >
        {isBaseRegion && (
          <Alert severity="info" sx={{ mt: 1 }}>
            This is a public base region. Its name and place in the location
            list drive the public pages, so only a developer can change them.
            You can still edit its additional names.
          </Alert>
        )}
        <TextField
          {...register('name', {
            required: 'This field is required',
          })}
          error={!!(errors as any)?.name}
          helperText={(errors as any)?.name?.message}
          margin="normal"
          fullWidth
          InputLabelProps={{ shrink: true }}
          type="text"
          label="Name"
          inputProps={{ readOnly: isBaseRegion }}
          name="name"
        />
        <TextField
          {...register('country', {
            required: 'This field is required',
          })}
          error={!!(errors as any)?.country}
          helperText={(errors as any)?.country?.message}
          margin="normal"
          fullWidth
          InputLabelProps={{ shrink: true }}
          type="text"
          label="Country"
          inputProps={{ readOnly: isBaseRegion }}
          name="country"
        />
        {isNewZealand ? (
          <Controller
            name="region"
            control={control}
            rules={{
              validate: () => {
                const name = getValues('name')
                const country = getValues('country')
                const regionVal = getValues('region')
                return (
                  !(
                    !isEmpty(regionVal) &&
                    (isEmpty(name) || isEmpty(country))
                  ) || 'You cannot fill Region if Country is empty'
                )
              },
            }}
            render={({ field }) => (
              <Autocomplete
                {...field}
                value={field.value || null}
                onChange={(_, value) => {
                  field.onChange(value)
                  // Clear council when region changes
                  setValue('council', '')
                }}
                options={[...NZ_REGIONS]}
                readOnly={isBaseRegion}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Region"
                    margin="normal"
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                    error={!!(errors as any)?.region}
                    helperText={(errors as any)?.region?.message}
                  />
                )}
              />
            )}
          />
        ) : (
          <TextField
            {...register('region', {
              validate: () => {
                const name = getValues('name')
                const country = getValues('country')
                const regionVal = getValues('region')
                return (
                  !(
                    !isEmpty(regionVal) &&
                    (isEmpty(name) || isEmpty(country))
                  ) || 'You cannot fill Region if Country is empty'
                )
              },
            })}
            error={!!(errors as any)?.region}
            helperText={(errors as any)?.region?.message}
            margin="normal"
            fullWidth
            InputLabelProps={{ shrink: true }}
            type="text"
            label="Region"
            name="region"
            inputProps={{ readOnly: isBaseRegion }}
          />
        )}
        {isNewZealand ? (
          <Controller
            name="council"
            control={control}
            rules={{
              validate: () => {
                const name = getValues('name')
                const country = getValues('country')
                const regionVal = getValues('region')
                const council = getValues('council')
                return (
                  !(
                    !isEmpty(council) &&
                    (isEmpty(name) || isEmpty(country) || isEmpty(regionVal))
                  ) || 'You cannot fill Council if Country or Region is empty'
                )
              },
            }}
            render={({ field }) => (
              <Autocomplete
                {...field}
                value={field.value || null}
                onChange={(_, value) => field.onChange(value)}
                options={councilOptions}
                disabled={!region}
                readOnly={isBaseRegion}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Council"
                    margin="normal"
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                    error={!!(errors as any)?.council}
                    helperText={
                      (errors as any)?.council?.message ||
                      (!region ? 'Select a region first' : '')
                    }
                  />
                )}
              />
            )}
          />
        ) : (
          <TextField
            {...register('council', {
              validate: () => {
                const name = getValues('name')
                const country = getValues('country')
                const regionVal = getValues('region')
                const council = getValues('council')
                return (
                  !(
                    !isEmpty(council) &&
                    (isEmpty(name) || isEmpty(country) || isEmpty(regionVal))
                  ) || 'You cannot fill Council if Country or Region is empty'
                )
              },
            })}
            error={!!(errors as any)?.council}
            helperText={(errors as any)?.council?.message}
            margin="normal"
            fullWidth
            InputLabelProps={{ shrink: true }}
            type="text"
            label="Council"
            name="council"
            inputProps={{ readOnly: isBaseRegion }}
          />
        )}
        <TextField
          {...register('major', {
            validate: () => {
              const name = getValues('name')
              const country = getValues('country')
              const regionVal = getValues('region')
              const council = getValues('council')
              const major = getValues('major')
              return (
                !(
                  !isEmpty(major) &&
                  (isEmpty(name) ||
                    isEmpty(country) ||
                    isEmpty(regionVal) ||
                    isEmpty(council))
                ) ||
                'You cannot fill Major if Country or Region or Council is empty'
              )
            },
          })}
          error={!!(errors as any)?.major}
          helperText={(errors as any)?.major?.message}
          margin="normal"
          fullWidth
          InputLabelProps={{ shrink: true }}
          type="text"
          label="Major (City, Town)"
          inputProps={{ readOnly: isBaseRegion }}
          name="major"
        />
        <TextField
          {...register('suburb', {
            validate: () => {
              const name = getValues('name')
              const country = getValues('country')
              const regionVal = getValues('region')
              const council = getValues('council')
              const major = getValues('major')
              const suburb = getValues('suburb')
              return (
                !(
                  !isEmpty(suburb) &&
                  (isEmpty(name) ||
                    isEmpty(country) ||
                    isEmpty(regionVal) ||
                    isEmpty(council) ||
                    isEmpty(major))
                ) ||
                'You cannot fill Suburb if Country or Region or Council or Major is empty'
              )
            },
          })}
          error={!!(errors as any)?.suburb}
          helperText={(errors as any)?.suburb?.message}
          margin="normal"
          fullWidth
          InputLabelProps={{ shrink: true }}
          type="text"
          label="Suburb"
          inputProps={{ readOnly: isBaseRegion }}
          name="suburb"
        />
        <TextField
          {...register('additional_names')}
          error={!!(errors as any)?.additional_names}
          helperText={(errors as any)?.additional_names?.message}
          margin="normal"
          fullWidth
          InputLabelProps={{ shrink: true }}
          type="text"
          label="Additional names (comma separated)"
          name="additional_names"
        />
      </Box>
    </Edit>
  )
}
